import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { FEATURE_PERMISSIONS, hasPermission } from '../utils/permissions';
import SystemSettingEditorModal from '../components/forms/SystemSettingEditorModal';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import PaginationControls from '../components/common/PaginationControls';
import AddonsPanel from '../components/common/AddonsPanel';
import { useDebounce } from '../hooks/useDebounce';

const SystemSettingsPage = ({ user }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [archiveFilter, setArchiveFilter] = useState('active'); // 'all', 'active', 'archived'
  const [showModal, setShowModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  const debouncedSearch = useDebounce(search, 350);
  const PAGE_SIZE = 25;
  
  // Check permission
  const canManageTemplates = hasPermission(user, FEATURE_PERMISSIONS.SYSTEM_SETTINGS_MANAGE) || hasPermission(user, FEATURE_PERMISSIONS.SYSTEM_SETTINGS_VIEW);
  const isReadOnly = !hasPermission(user, FEATURE_PERMISSIONS.SYSTEM_SETTINGS_MANAGE);
  
  // Fetch templates
  useEffect(() => {
    if (!canManageTemplates) {
      setLoading(false);
      return;
    }
    
    const q = query(
      collection(db, collectionPath('systemSettings')),
      orderBy('category', 'asc'),
      orderBy('name', 'asc')
    );
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const templateData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTemplates(templateData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching templates:', error);
        setToast({ message: `Error: ${error.message}`, type: 'error' });
        setLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [canManageTemplates]);
  
  // Filter templates
  const filteredTemplates = useMemo(() => {
    let filtered = templates;
    
    // Archive filter
    if (archiveFilter === 'active') {
      filtered = filtered.filter(t => !t.isArchived);
    } else if (archiveFilter === 'archived') {
      filtered = filtered.filter(t => t.isArchived);
    }
    // 'all' shows everything
    
    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }
    
    // Search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter(t => 
        t.name?.toLowerCase().includes(searchLower) ||
        t.templateId?.toLowerCase().includes(searchLower) ||
        t.description?.toLowerCase().includes(searchLower) ||
        t.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }
    
    return filtered;
  }, [templates, categoryFilter, archiveFilter, debouncedSearch]);
  
  // Pagination
  const paginatedTemplates = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredTemplates.slice(start, start + PAGE_SIZE);
  }, [filteredTemplates, currentPage]);
  
  const totalPages = Math.ceil(filteredTemplates.length / PAGE_SIZE);
  
  // Calculate counts for tabs
  const activeCount = useMemo(() => templates.filter(t => !t.isArchived).length, [templates]);
  const archivedCount = useMemo(() => templates.filter(t => t.isArchived).length, [templates]);
  const allCount = templates.length;
  
  // Handlers
  const handleCreate = () => {
    setEditTemplate(null);
    setShowModal(true);
  };
  
  const handleEdit = (template) => {
    setEditTemplate(template);
    setShowModal(true);
  };
  
  const handleDelete = async (template) => {
    const confirmed = window.confirm(
      `Delete setting "${template.name}"?\n\nThis action cannot be undone and may break scripts or features using this setting.`
    );
    
    if (!confirmed) return;
    
    try {
      await deleteDoc(doc(db, collectionPath('systemSettings'), template.id));
      setToast({ 
        message: `Setting "${template.name}" deleted successfully`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error deleting template:', error);
      setToast({ message: `Failed to delete: ${error.message}`, type: 'error' });
    }
  };
  
  const handleToggleArchive = async (template) => {
    const { updateDoc } = await import('firebase/firestore');
    const newArchiveStatus = !template.isArchived;
    
    try {
      await updateDoc(doc(db, collectionPath('systemSettings'), template.id), {
        isArchived: newArchiveStatus,
        updatedAt: new Date(),
        updatedBy: user?.uid || 'unknown',
        updatedByName: user?.displayName || 'Unknown User'
      });
      
      setToast({ 
        message: `Setting ${newArchiveStatus ? 'archived' : 'unarchived'} successfully`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error toggling archive status:', error);
      setToast({ message: `Error: ${error.message}`, type: 'error' });
    }
  };
  
  const handleSaved = (message) => {
    setShowModal(false);
    setEditTemplate(null);
    setToast({ message, type: 'success' });
  };
  
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'email': return '📧';
      case 'script_variable': return '📝';
      case 'config': return '🔧';
      default: return '📄';
    }
  };
  
  const getCategoryLabel = (category) => {
    switch (category) {
      case 'email': return 'Email Template';
      case 'script_variable': return 'Script Variable';
      case 'config': return 'Configuration';
      default: return category;
    }
  };
  
  const getTypeLabel = (type) => {
    switch (type) {
      case 'html': return 'HTML';
      case 'text': return 'Text';
      case 'json': return 'JSON';
      case 'number': return 'Number';
      case 'string': return 'String';
      case 'boolean': return 'Boolean';
      default: return type;
    }
  };
  
  // Permission check
  if (!canManageTemplates) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-semibold">Access Denied</p>
          <p className="text-red-600 text-sm mt-1">
            You don't have permission to manage system settings. This feature is admin-only.
          </p>
        </div>
      </main>
    );
  }
  
  if (loading) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading settings...</div>
        </div>
      </main>
    );
  }
  
  return (
    <main className="flex-grow p-6 overflow-y-auto">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      
      <div className="flex justify-between items-center mb-6">
        <div className="flex-1 pr-6">
          <h2 className="text-2xl font-bold text-gray-900">System Settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage email templates, script variables, and system configurations.
          </p>
          {/* WARNING: Changing these settings can break the app */}
          <div className="mt-4 bg-red-50 border border-red-200 text-red-800 p-3 rounded mb-2 max-w-6xl break-words">
            <div className="font-semibold text-sm">⚠️ Warning: Changing System Settings Can Break The App</div>
            <div className="text-sm mt-1">
              These settings are used by background scripts, scheduled jobs, and core pages. Modifying,
              archiving, or deleting values (for example: lookback windows, page sizes, notification
              recipients, or template IDs) may cause sync failures, missing notifications, or UI errors.
              Only administrators who understand the impact should edit these values. <p></p><p></p>When in doubt,
              consult the developer team or review the <span className="font-medium">System Settings </span>
              documentation before making changes.
            </div>
          </div>
        </div>
        
        {!isReadOnly && (
        <Tooltip text="Create a new template or variable">
          <button
            onClick={handleCreate}
            className="flex-shrink-0 z-20 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors duration-150 flex items-center gap-2"
          >
            <span className="text-lg">+</span>
            New Setting
          </button>
        </Tooltip>
        )}
      </div>
      
      <AddonsPanel onToast={setToast} />
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-4">
          {/* Category filter */}
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">All Categories</option>
              <option value="email">📧 Email Templates</option>
              <option value="script_variable">📝 Script Variables</option>
              <option value="config">🔧 Configurations</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 transition-colors duration-150 ${
                search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
              }`}
            />
          </div>

          {/* Reset Filters Button */}
        {(search || categoryFilter !== 'all' || archiveFilter !== 'active') && (
          <div className="flex flex-wrap gap-4">
            <Tooltip text="Clear filters and reset view">
              <button
                onClick={() => {
                  setSearch('');
                  setCategoryFilter('all');
                  setArchiveFilter('active');
                  setCurrentPage(1);
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-150 text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset Filters
              </button>
            </Tooltip>
          </div>
        )}
        </div>
        
        {/* Archive tabs */}
        <div className="flex gap-2 mt-4 border-b border-gray-200">
          <button
            onClick={() => setArchiveFilter('active')}
            className={`px-4 py-2 font-semibold text-sm transition-colors duration-150 border-b-2 ${
              archiveFilter === 'active'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Active <span className="ml-1 text-xs opacity-75">({activeCount})</span>
          </button>
          <button
            onClick={() => setArchiveFilter('archived')}
            className={`px-4 py-2 font-semibold text-sm transition-colors duration-150 border-b-2 ${
              archiveFilter === 'archived'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Archived <span className="ml-1 text-xs opacity-75">({archivedCount})</span>
          </button>
          <button
            onClick={() => setArchiveFilter('all')}
            className={`px-4 py-2 font-semibold text-sm transition-colors duration-150 border-b-2 ${
              archiveFilter === 'all'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            All <span className="ml-1 text-xs opacity-75">({allCount})</span>
          </button>
        </div>
      </div>
      
      {/* Templates table */}
      {paginatedTemplates.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-gray-400 text-5xl mb-4">📝</div>
          <p className="text-gray-600 font-semibold">No templates found</p>
          <p className="text-gray-500 text-sm mt-2">
            {search || categoryFilter !== 'all' 
              ? 'Try adjusting your filters' 
              : 'Create your first template to get started'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Template ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Updated</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedTemplates.map(template => (
                    <tr 
                      key={template.id} 
                      className={`hover:bg-gray-50 transition-colors ${template.isArchived ? 'bg-amber-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getCategoryIcon(template.category)}</span>
                          <div>
                            <div 
                              className="font-medium text-gray-900 hover:text-indigo-600 cursor-pointer"
                              onClick={() => handleEdit(template)}
                              title="Click to edit"
                            >
                              {template.name}
                            </div>
                            {template.description && (
                              <Tooltip text={template.description} maxWidth={400}>
                                <div className="text-xs text-gray-500 truncate max-w-[200px] cursor-help">
                                  {template.description}
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{getCategoryLabel(template.category)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 font-mono">{getTypeLabel(template.type)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {template.templateId ? (
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200 text-gray-700">
                            {template.templateId}
                          </code>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {template.isArchived ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                            Archived
                          </span>
                        ) : (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            template.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {template.isActive ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-500">
                          {template.updatedAt 
                            ? new Date(typeof template.updatedAt.toDate === 'function' ? template.updatedAt.toDate() : template.updatedAt).toLocaleDateString()
                            : template.createdAt 
                              ? new Date(typeof template.createdAt.toDate === 'function' ? template.createdAt.toDate() : template.createdAt).toLocaleDateString()
                              : '-'
                          }
                        </div>
                        {template.updatedByName && (
                          <div className="text-xs text-gray-400 truncate max-w-[100px]" title={template.updatedByName}>
                            by {template.updatedByName}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          {!isReadOnly && (<>
                          <Tooltip text="Edit">
                            <button
                              onClick={() => handleEdit(template)}
                              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          </Tooltip>
                          <Tooltip text={template.isArchived ? 'Unarchive' : 'Archive'}>
                            <button
                              onClick={() => handleToggleArchive(template)}
                              className={`p-1.5 rounded transition-colors ${
                                template.isArchived
                                  ? 'text-green-600 hover:bg-green-50'
                                  : 'text-amber-600 hover:bg-amber-50'
                              }`}
                            >
                              {template.isArchived ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                </svg>
                              )}
                            </button>
                          </Tooltip>
                          <Tooltip text="Delete (may break scripts!)">
                            <button
                              onClick={() => handleDelete(template)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </Tooltip>
                          </>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination */}
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} className="mt-6" />
        </>
      )}
      
      {/* Editor modal */}
      {showModal && (
        <SystemSettingEditorModal
          template={editTemplate}
          onClose={() => {
            setShowModal(false);
            setEditTemplate(null);
          }}
          onSaved={handleSaved}
          user={user}
        />
      )}
    </main>
  );
};

export default SystemSettingsPage;
